import { t } from '@i18n';
import isNil from 'lodash/isNil';
import * as yup from 'yup';
import { MixedSchema } from 'yup/lib/mixed';

declare module 'yup' {
  interface ObjectSchema<TShape, TContext, TIn, TOut> {
    /**
     * This method ensures that all fields in an object are either all filled or all nil.
     * If one field is filled, all fields must be filled.
     * If one field is nil, all fields must be nil.
     *
     * @example
     *
     * yup.object({
     *   name: yup.string().required(),
     *   age: yup.number().required(),
     * }).allOrNone(t('General_Required'));
     */
    allOrNone(message: string, excludeFields?: string[]): ObjectSchema<TShape, TContext, TIn, TOut>;
  }

  interface NumberSchema {
    /**
     * This method transforms an empty string to null.
     * If nullable is true, the schema will be nullable. (default option)
     * If nullable is false, the schema will be required.
     *
     * Note: When passing the default value, you can use getEmptyValue from formUtils.ts
     *
     * @example
     *
     * ```ts
     * const schema = yup.object({
     *   economicUnitId: yup.number().emptyStringToNull(),
     * });
     *
     * const methods = useForm<yup.InferType<typeof schema>>({
     *   defaultValues: {
     *     economicUnitId: getEmptyValue(),
     *   },
     *   resolver: yupResolver(schema),
     * });
     * ```
     */
    emptyStringToNull<Required extends boolean = false>(
      required?: Required,
    ): Required extends true
      ? yup.NumberSchema<number, any, number>
      : yup.NumberSchema<number | null | undefined, any, number | null | undefined>;
  }

  interface StringSchema {
    /**
     * This method transforms a null or undefined value to an empty string.
     * If required is false, the schema will be nullable. (default option)
     * If required is true, the schema will be required.
     *
     * @example
     *
     * ```ts
     * const schema = yup.object({
     *   role_description: yup.string().nilToEmptyString(),
     * });
     * ```
     */
    nilToEmptyString<Required extends boolean = false>(
      required?: Required,
    ): Required extends true
      ? yup.StringSchema<string, any, string>
      : yup.StringSchema<string | null | undefined, any, string | null | undefined>;
  }
}

yup.addMethod<yup.AnyObjectSchema>(
  yup.object,
  'allOrNone',
  function (this: yup.AnyObjectSchema, message: string, excludeFields: string[] = ['id']) {
    return this.test('allOrNone', message, function (values: Record<string, unknown>) {
      const allFields = Object.keys(this.schema.fields).filter((field) => !excludeFields.includes(field));

      // If all values are nil, we don't need to add a required error on any of them
      const anyFilled = allFields.some((item) => !isNil(values[item]));
      if (!anyFilled) {
        return true;
      }

      const errors: yup.ValidationError[] = [];
      allFields.forEach((key) => {
        // If the value is not nil, we don't need to add a required error
        if (!isNil(values[key])) {
          return;
        }

        errors.push(
          this.createError({
            path: `${this.path}.${key}`,
            message,
          }),
        );
      });

      if (errors.length) {
        // Throw a ValidationError with the array of field-level errors
        throw new yup.ValidationError(errors);
      }

      return true;
    });
  },
);

(yup.addMethod as any)(yup.number, 'emptyStringToNull', function (this: yup.NumberSchema, required: boolean = false) {
  const transformed = this.transform((_: unknown, originalValue: number | '' | null | undefined) => {
    if ((originalValue as unknown as string) === '') {
      return null;
    }

    return originalValue;
  })
    .default('' as unknown as number)
    .nullable();

  return required ? transformed.required() : transformed;
});

(yup.addMethod as any)(yup.string, 'nilToEmptyString', function (this: yup.StringSchema, required: boolean = false) {
  const transformed = this.transform((_: unknown, originalValue: null | undefined) => {
    if (isNil(originalValue)) {
      return '';
    }

    return originalValue;
  })
    .default('')
    .nullable();

  return required ? transformed.required() : transformed;
});

/**
 * Filters out objects with all nil values from an array
 */
export const filterEmptyObjects = <T extends Record<string, unknown>>(array: T[]) => {
  return array.filter((item) => {
    return Object.values(item).some((value) => !isNil(value));
  });
};

export const yupNativeEnum = <T extends object, Nullable extends boolean | undefined = boolean>(
  enumValue: T,
  message?: string,
  { nullable = true, emptyStringToNull = true }: { nullable?: Nullable; emptyStringToNull?: boolean } = {},
): Nullable extends true ? MixedSchema<T[keyof T] | null | undefined> : MixedSchema<T[keyof T] | undefined> => {
  return yup
    .mixed<T[keyof T]>()
    .oneOf([...Object.values(enumValue), ...(nullable ? [null] : [])], message ?? t('General_Required'))
    .nullable(nullable as unknown as false)
    .when([], {
      is: () => emptyStringToNull,
      then: (schema) =>
        schema.transform((value, originalValue) => (originalValue === '' ? null : value)).default('' as any),
    });
};

/**
 * This array schema removes objects with all nil values from an array
 *
 * if skipFiltering is true, the schema will not remove any objects
 * context: {
 *   skipFiltering: true,
 * }
 *
 * @param keysToCheck - The keys to check for nil values
 * @example
 *
 * ```ts
 * const schema = yup.array(yup.object({ a: yup.string().required(), b: yup.number().nullable() })).removeEmptyObjects(['a', 'b']);
 * ```
 */
export const yupArrayWithEmptyItems = <T extends yup.AnyObjectSchema>(
  schema: T,
  keysToCheck: (keyof yup.TypeOf<T>)[],
) => {
  return yup.array(schema).when('$skipFiltering', {
    is: (skipFiltering: boolean | undefined) => typeof skipFiltering !== 'boolean' || !skipFiltering,
    then: (arraySchema) =>
      arraySchema.transform((values: any[]) => {
        return values.filter((value) => {
          return keysToCheck.some((key) => !isNil(value[key]) && (value[key] as unknown as string) !== '');
        });
      }),
  });
};
